import {CommandQueueState, isQueueStateEvent, type QueueStateEvent} from '@perfectpost/perfect-post-common';

import {type ExtensionTransport} from './transport';

// File de commandes de l'extension, sortie de Redux. Cœur pur (aucun accès `window` direct) :
// dépend du transport injecté et d'un `isAvailable` (fourni par le domaine connexion), ce qui
// rend la file testable sans extension.

export type QueueCommand = {commandId: string; finished: boolean};

export type CommandQueueSnapshot = {
  commandStack: QueueCommand[];
  currentCommandId?: string;
  state: CommandQueueState;
};

export type ExtensionUnavailable = {status: 'extension_not_found'};
export type EnqueueResult = {status: 'sent'; commandId?: string} | ExtensionUnavailable;
export type CheckQueueResult = {status: 'sent'} | ExtensionUnavailable;

export type ExtensionCommandQueue = {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => CommandQueueSnapshot;
  // Demande à l'extension de traiter/rapporter sa file. Refus immédiat si extension indisponible.
  checkQueue: () => Promise<CheckQueueResult>;
  // Enqueue une commande : garde la disponibilité, exécute l'envoi fourni, puis relance la file.
  // Le `send` typé est fourni par l'appelant (la file reste agnostique des types de commande).
  enqueue: (send: () => Promise<string | undefined>) => Promise<EnqueueResult>;
  dismissFinishedCommand: (commandId: string) => void;
};

type ExtensionCommandQueueDeps = {
  transport: ExtensionTransport;
  isAvailable: () => boolean;
};

const EMPTY_SNAPSHOT: CommandQueueSnapshot = {commandStack: [], state: CommandQueueState.Idle};

// Reprend, en immuable, la logique de l'ancien reducer Redux `syncQueueState`.
export function reduceQueueEvent(snapshot: CommandQueueSnapshot, event: QueueStateEvent): CommandQueueSnapshot {
  const payload = event.payload;
  let commandStack = snapshot.commandStack;

  if (
    snapshot.state === CommandQueueState.Processing &&
    snapshot.currentCommandId !== undefined &&
    snapshot.currentCommandId !== payload.currentCommandId
  ) {
    commandStack = commandStack.map((command) =>
      command.commandId === snapshot.currentCommandId ? {...command, finished: true} : command,
    );
  } else if (
    snapshot.state === CommandQueueState.Idle &&
    snapshot.currentCommandId === undefined &&
    payload.currentCommandId !== undefined
  ) {
    commandStack = [...commandStack, {commandId: payload.currentCommandId, finished: false}];
  }

  const known = new Set(commandStack.map((command) => command.commandId));
  const appended = payload.commandStack
    .filter((commandId) => !known.has(commandId))
    .map((commandId) => ({commandId, finished: false}));

  return {
    state: payload.state,
    currentCommandId: payload.currentCommandId,
    commandStack: appended.length > 0 ? [...commandStack, ...appended] : commandStack,
  };
}

export function createExtensionCommandQueue({
  transport,
  isAvailable,
}: ExtensionCommandQueueDeps): ExtensionCommandQueue {
  let snapshot: CommandQueueSnapshot = EMPTY_SNAPSHOT;
  const listeners = new Set<() => void>();

  const setSnapshot = (next: CommandQueueSnapshot) => {
    if (next === snapshot) return;
    snapshot = next;
    listeners.forEach((listener) => listener());
  };

  transport.subscribe((event) => {
    if (isQueueStateEvent(event.data)) {
      setSnapshot(reduceQueueEvent(snapshot, event.data));
    }
  });

  const requestQueueState = () => transport.request({source: 'perfectpost', type: 'check_queue'});

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
    checkQueue: async () => {
      if (!isAvailable()) return {status: 'extension_not_found'};
      // Les appelants font `void checkQueue()` : on ne laisse jamais la promesse rejeter
      // (timeout/erreur extension) pour éviter une unhandled rejection.
      await requestQueueState().catch(() => undefined);
      return {status: 'sent'};
    },
    enqueue: async (send) => {
      if (!isAvailable()) return {status: 'extension_not_found'};
      const commandId = await send();
      // Relance la file pour que l'extension prenne la commande fraîchement créée.
      await requestQueueState().catch(() => undefined);
      return {status: 'sent', commandId};
    },
    dismissFinishedCommand: (commandId) => {
      const commandStack = snapshot.commandStack.filter((command) => command.commandId !== commandId);
      if (commandStack.length !== snapshot.commandStack.length) {
        setSnapshot({...snapshot, commandStack});
      }
    },
  };
}
